/**
 * Sprach_Umschalter (LanguageSwitcher).
 *
 * Bietet genau die Unterstützten_Sprachen (`de`, `en`) als exklusive
 * `ToggleButtonGroup` an (Req 4.1) und markiert die Aktive_Sprache visuell sowie
 * programmatisch via `aria-pressed` (Req 4.4) — MUI setzt `aria-pressed` am
 * selektierten `ToggleButton` automatisch.
 *
 * Persistenz-Verhalten (Design-Komponente "LanguageSwitcher"):
 * - eingeloggt  → `auth.setLanguagePreference(lang)` (Sprach_Cache + Backend, Req 4.2/7.1)
 * - anonym      → `i18n.setLanguage(lang)` (nur Sprach_Cache, KEIN Backend-Aufruf, Req 8.4)
 *
 * Die Beschriftungen sind feste Endonyme ("Deutsch"/"English"), da Sprachnamen
 * üblicherweise in der jeweiligen Sprache selbst dargestellt werden.
 */

import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { useI18n } from "../i18n/I18nProvider";
import { useAuth } from "../contexts/AuthContext";
import { SUPPORTED_LANGUAGES, type Language } from "../i18n/catalog";

// ---------------------------------------------------------------------------
// Beschriftungen
// ---------------------------------------------------------------------------

/** Endonyme Beschriftungen je Unterstützter_Sprache. */
const LANGUAGE_LABELS: Record<Language, string> = {
  de: "Deutsch",
  en: "English",
};

/** Kurzform (für ARIA bzw. kompakte Darstellung). */
const LANGUAGE_SHORT: Record<Language, string> = {
  de: "DE",
  en: "EN",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function LanguageSwitcher() {
  const { language, setLanguage, t } = useI18n();
  const { user, setLanguagePreference } = useAuth();

  const handleSelect = (_e: React.MouseEvent<HTMLElement>, lang: Language | null) => {
    // Exklusive Gruppe: `null` bei Klick auf die bereits aktive Sprache
    // ignorieren (kein Umschalten) — entspricht dem bisherigen Verhalten.
    if (lang === null || lang === language) return;
    if (user) {
      // Eingeloggt: Sprach_Cache + Backend (optimistisch, Req 4.2/7.1).
      // Fehler beim Persistieren lassen die Aktive_Sprache dennoch gesetzt.
      void setLanguagePreference(lang);
    } else {
      // Anonym: nur Sprach_Cache, KEIN Backend-Aufruf (Req 8.4).
      setLanguage(lang);
    }
  };

  return (
    <ToggleButtonGroup
      exclusive
      size="small"
      value={language}
      onChange={handleSelect}
      aria-label={t("languageSwitcher.groupAria")}
    >
      {SUPPORTED_LANGUAGES.map((lang) => (
        <ToggleButton
          key={lang}
          value={lang}
          lang={lang}
          aria-label={`${LANGUAGE_LABELS[lang]} (${LANGUAGE_SHORT[lang]})`}
          sx={{ minWidth: 44, minHeight: 40 }}
        >
          {LANGUAGE_SHORT[lang]}
        </ToggleButton>
      ))}
    </ToggleButtonGroup>
  );
}
